import { displayDecimalFormatted } from "../../../../utils/systemSettings";

// Dynamic K/M/B compaction so a $100,000,000 budget renders as "$100M" instead of overflowing a
// card value or a chart axis tick - the mantissa still goes through displayDecimalFormatted so it
// respects the account's configured decimal/grouping separators.
export function formatCompactMoney(settings, currency, value) {
  const val = Number(value) || 0;
  const abs = Math.abs(val);
  let divisor = 1;
  let suffix = "";
  if (abs >= 1_000_000_000) {
    divisor = 1_000_000_000;
    suffix = "B";
  } else if (abs >= 1_000_000) {
    divisor = 1_000_000;
    suffix = "M";
  } else if (abs >= 1_000) {
    divisor = 1_000;
    suffix = "K";
  }
  return `${currency} ${displayDecimalFormatted(settings, val / divisor)}${suffix}`;
}

// Same compaction, no currency prefix - for large non-money magnitudes (e.g. resource hours).
export function formatCompactNumber(settings, value) {
  const val = Number(value) || 0;
  const abs = Math.abs(val);
  let divisor = 1;
  let suffix = "";
  if (abs >= 1_000_000_000) {
    divisor = 1_000_000_000;
    suffix = "B";
  } else if (abs >= 1_000_000) {
    divisor = 1_000_000;
    suffix = "M";
  } else if (abs >= 1_000) {
    divisor = 1_000;
    suffix = "K";
  }
  return `${displayDecimalFormatted(settings, val / divisor)}${suffix}`;
}

export const STATUS_MAP = [
  { name: "On Target", color: "#0d9f61" },
  { name: "Monitoring", color: "#eab308" },
  { name: "Slipping", color: "#dc2626" },
  { name: "Completed", color: "#342c82" },
  { name: "On Hold", color: "#9e9e9e" },
];

export const RISK_SEVERITY_LABELS = ["Low", "Moderate", "High"];
export const RISK_SEVERITY_COLORS = ["#0B6A0B", "#EAA300", "#C50F1F"];

export function buildStatusSegments(projectsByStatus = []) {
  const total = projectsByStatus.reduce((sum, s) => sum + s.count, 0);
  if (!total) {
    return STATUS_MAP.map((s) => ({ name: s.name, color: s.color, range: 100 / STATUS_MAP.length }));
  }
  return STATUS_MAP
    .map((meta, i) => {
      const found = projectsByStatus.find((s) => s.status === i);
      return { name: meta.name, color: meta.color, value: found?.count ?? 0, range: Math.round(((found?.count ?? 0) / total) * 100) };
    })
    .filter((s) => s.range > 0);
}

export function buildRiskSeverityRows(risksBySeverity = []) {
  const total = risksBySeverity.reduce((sum, s) => sum + s.count, 0);
  return RISK_SEVERITY_LABELS.map((label, i) => {
    const count = risksBySeverity.find((s) => s.status === i)?.count ?? 0;
    return { label, color: RISK_SEVERITY_COLORS[i], count, pct: total > 0 ? Math.round((count / total) * 100) : 0 };
  });
}

// Shared copy for the info-icon tooltips on KPI cards, both the top-level ones and the
// per-group cards - keeps the wording (and the calculation it documents) in one place.
export const KPI_INFO = {
  health: "Rolls up every project's current health status (On Target, Monitoring, or Slipping) into a single average.",
  trending: "Same rollup as Health, but using each project's forecasted status for the next period - shows whether things are trending better or worse.",
  overallProgress: "Average completion percentage across all projects in scope.",
  activeProjects: "Projects that are not yet Completed or On Hold, out of the total project count.",
  onTimeDelivery: "Share of completed projects that finished on time - their revised end date (if any) was not later than the originally planned end date.",
  grossMargin: "(Invoiced − Actual cost) ÷ Invoiced, as a percentage. How much margin has been realized on what's been billed so far.",
  atRisk: "Projects whose forecasted health for next period is worse than their current actual health.",
  budget: "Budget vs Spent vs ETC (estimate to complete) vs BAC (budget at completion), summed across all projects in scope.",
  risk: "Open risks bucketed by severity score: Low (≤ 8), Moderate (≤ 16), High (> 16).",
  projectsByStatus: "Every project in scope, grouped by its current status.",
  valueDelivered: "Planned vs actual value delivered each month, cumulative, plus the budget burn rate - the pace of spend against budget.",
  valueDeliveredSoFar: "Cumulative actual value delivered within the selected period (or year to date if no period is set).",
  resourceCapacity: "Planned vs actual (logged) resource hours per month across all projects in scope.",
};
